import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { logger } from '../utils/logger';
import { setMetaTags, resetMetaTags, buildCanonicalUrl, PAGE_SEO } from '../utils/seo';

interface Article {
  id: string;
  title: string;
  slug: string;
  preview?: string;
  excerpt?: string;
  published_at: string;
  author?: string;
  author_id?: string;
  cover_image?: string;
}

interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface ArticlesResponse {
  success: boolean;
  data: Article[];
  pagination: PaginationInfo;
}

const ArticlesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentPage = Number(searchParams.get('page')) || 1;

  useEffect(() => {
    setMetaTags({ ...PAGE_SEO.articles, canonicalUrl: buildCanonicalUrl('/articles') });
    return () => { resetMetaTags(); };
  }, []);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = searchParams.get('q');
      const page = searchParams.get('page') || '1';
      const url = q
        ? `/articles/search?q=${encodeURIComponent(q)}&page=${page}&pageSize=10`
        : `/articles?page=${page}&pageSize=10`;

      const res = await api.get<ArticlesResponse>(url);
      if (!res.success) { setError(res.error || 'Не удалось загрузить статьи'); return; }

      const articlesResponse = res.data as ArticlesResponse | undefined;
      let rawArticles: Article[] = [];
      let paginationData: PaginationInfo | null = null;

      if (articlesResponse && Array.isArray(articlesResponse.data)) {
        rawArticles = articlesResponse.data;
        paginationData = articlesResponse.pagination ?? null;
      } else if (Array.isArray(res.data)) {
        rawArticles = res.data as unknown as Article[];
      }

      const mapped = rawArticles.map((a: any) => ({
        id: a.id, title: a.title, slug: a.slug,
        preview: a.excerpt || a.preview || '',
        published_at: a.published_at || a.created_at,
        author: a.author || 'Astranum',
        cover_image: a.cover_image || '',
      }));

      setArticles(mapped);
      setPagination(paginationData);
    } catch {
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ q: searchQuery, page: '1' });
  };

  const handlePageChange = (newPage: number) => {
    const q = searchParams.get('q');
    setSearchParams({ ...(q && { q }), page: String(newPage) });
  };

  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  const resolveCoverSrc = (coverImage: string | undefined, slug: string): string | null => {
    if (!coverImage) return null;
    if (coverImage.trimStart().startsWith('<svg')) {
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(coverImage)}`;
    }
    if (coverImage.startsWith('/') || coverImage.startsWith('http')) return coverImage;
    return null;
  };

  return (
    <div style={{
      minHeight: '80vh',
      padding: isMobile ? '20px 12px' : '40px 20px',
      maxWidth: '900px',
      margin: '0 auto',
      position: 'relative',
    }}>
      <style>{`
        @keyframes articleFadeIn {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes goldShimmer {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 200% center; }
        }
        .article-row {
          animation: articleFadeIn 0.5s ease-out both;
        }
        .article-row:hover .article-title {
          color: #d4af37 !important;
        }
        .article-row:hover .article-arrow {
          transform: translateX(6px);
          opacity: 1;
        }
        .article-row:hover {
          background: rgba(212, 175, 55, 0.04) !important;
        }
      `}</style>

      {/* Заголовок */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
        <h1 style={{
          fontSize: isMobile ? 'clamp(28px, 5vw, 36px)' : 'clamp(36px, 5vw, 52px)',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '12px',
          animation: 'goldShimmer 3s ease-in-out infinite',
        }}>
          Библиотека знаний
        </h1>
        <p style={{ color: '#888', fontSize: '16px', fontFamily: 'var(--font-sans)' }}>
          Астрология, нумерология и самопознание
        </p>
      </div>

      {/* Поиск */}
      <form onSubmit={handleSearch} style={{
        display: 'flex', gap: '8px', marginBottom: '48px', maxWidth: '560px', margin: '0 auto 48px',
      }}>
        <input
          type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Найти статью..."
          style={{
            flex: 1, padding: '14px 20px',
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,175,55,0.2)',
            borderRadius: '30px', color: '#fff', fontSize: '15px', fontFamily: 'var(--font-sans)',
            outline: 'none', transition: 'border-color 0.3s',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.5)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.2)'; }}
        />
        <button type="submit" style={{
          background: 'linear-gradient(135deg, #d4af37, #f4d03f)', border: 'none',
          padding: '14px 28px', borderRadius: '30px', color: '#0a0a1a', fontSize: '15px',
          fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)', transition: 'all 0.3s',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,175,55,0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >Найти</button>
      </form>

      {loading ? (
        <LoadingState text="Загружаем статьи..." />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchArticles} />
      ) : articles.length === 0 ? (
        <div style={{
          background: 'rgba(13,20,40,0.85)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212,175,55,0.12)', borderRadius: '24px',
          padding: isMobile ? '40px 20px' : '60px 40px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📝</div>
          <h2 style={{ color: '#d4af37', fontSize: '24px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '12px' }}>
            Статьи скоро появятся
          </h2>
          <p style={{ color: '#b0b0b0', fontSize: '16px', lineHeight: 1.6 }}>
            Мы готовим для вас интересные материалы об астрологии и нумерологии.
          </p>
        </div>
      ) : (
        <>
          {/* Список статей */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {articles.map((article, idx) => {
              const coverSrc = resolveCoverSrc(article.cover_image, article.slug);
              const isFeatured = idx === 0 && currentPage === 1 && !searchParams.get('q');

              return (
                <div
                  key={article.id}
                  className="article-row"
                  onClick={() => navigate(`/articles/${article.slug}`)}
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '16px' : '24px',
                    padding: isMobile ? '20px 16px' : '28px 24px',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(212,175,55,0.08)',
                    transition: 'background 0.3s ease',
                    animationDelay: `${idx * 0.08}s`,
                  }}
                >
                  {/* Обложка / номер */}
                  {coverSrc ? (
                    <div style={{
                      flexShrink: 0,
                      width: isMobile ? '100%' : (isFeatured ? '280px' : '180px'),
                      height: isMobile ? '180px' : (isFeatured ? '200px' : '130px'),
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: 'rgba(13,20,40,0.6)',
                    }}>
                      <img src={coverSrc} alt={article.title} style={{
                        width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                        transition: 'transform 0.4s ease',
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      flexShrink: 0,
                      width: isMobile ? '48px' : (isFeatured ? '80px' : '56px'),
                      height: isMobile ? '48px' : (isFeatured ? '80px' : '56px'),
                      borderRadius: '50%',
                      background: isFeatured
                        ? 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(212,175,55,0.05))'
                        : 'rgba(212,175,55,0.06)',
                      border: '1px solid rgba(212,175,55,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#d4af37', fontSize: isFeatured ? '28px' : '20px',
                      fontFamily: 'var(--font-display)', fontStyle: 'italic',
                    }}>
                      {idx + 1 + (currentPage - 1) * 10}
                    </div>
                  )}

                  {/* Контент */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isFeatured && (
                      <span style={{
                        display: 'inline-block', fontSize: '11px', fontWeight: 600,
                        color: '#d4af37', textTransform: 'uppercase', letterSpacing: '1.5px',
                        marginBottom: '8px', fontFamily: 'var(--font-sans)',
                      }}>Избранное</span>
                    )}
                    <h2 className="article-title" style={{
                      color: '#f0f0f0',
                      fontSize: isFeatured ? (isMobile ? '22px' : '26px') : (isMobile ? '17px' : '20px'),
                      fontFamily: 'var(--font-display)',
                      fontStyle: 'italic',
                      lineHeight: 1.35,
                      marginBottom: '10px',
                      transition: 'color 0.3s ease',
                    }}>
                      {article.title}
                    </h2>
                    {article.preview && (
                      <p style={{
                        color: '#999', fontSize: isFeatured ? '15px' : '14px',
                        lineHeight: 1.65, fontFamily: 'var(--font-sans)',
                        marginBottom: '14px',
                        display: '-webkit-box', WebkitLineClamp: isFeatured ? 3 : 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {article.preview}
                      </p>
                    )}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '16px',
                      fontSize: '13px', color: '#666', fontFamily: 'var(--font-sans)',
                    }}>
                      <span>{formatDate(article.published_at)}</span>
                      <span style={{ color: 'rgba(212,175,55,0.3)' }}>•</span>
                      <span>{article.author}</span>
                      <span className="article-arrow" style={{
                        marginLeft: 'auto', color: '#d4af37', fontSize: '18px',
                        opacity: 0.4, transition: 'all 0.3s ease',
                      }}>→</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Пагинация */}
          {pagination && pagination.totalPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              gap: '8px', marginTop: '48px',
            }}>
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}
                style={{
                  background: 'none', border: '1px solid rgba(212,175,55,0.2)',
                  color: currentPage <= 1 ? '#444' : '#d4af37', fontSize: '14px',
                  padding: '10px 18px', borderRadius: '30px', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-sans)', transition: 'all 0.3s',
                }}>← Назад</button>

              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === pagination.totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | string)[]>((acc, p, idx, arr) => {
                  if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p); return acc;
                }, [])
                .map((item, idx) =>
                  typeof item === 'string'
                    ? <span key={`e-${idx}`} style={{ color: '#555', padding: '0 4px' }}>…</span>
                    : (
                      <button key={item} onClick={() => handlePageChange(item)} style={{
                        background: item === currentPage ? 'linear-gradient(135deg, #d4af37, #f4d03f)' : 'none',
                        border: item === currentPage ? 'none' : '1px solid rgba(212,175,55,0.15)',
                        color: item === currentPage ? '#0a0a1a' : '#d4af37',
                        fontSize: '14px', fontWeight: item === currentPage ? 700 : 400,
                        padding: '10px 16px', borderRadius: '30px', cursor: 'pointer',
                        fontFamily: 'var(--font-sans)', transition: 'all 0.3s', minWidth: '40px',
                      }}>{item}</button>
                    )
                )}

              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pagination.totalPages}
                style={{
                  background: 'none', border: '1px solid rgba(212,175,55,0.2)',
                  color: currentPage >= pagination.totalPages ? '#444' : '#d4af37', fontSize: '14px',
                  padding: '10px 18px', borderRadius: '30px', cursor: currentPage >= pagination.totalPages ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-sans)', transition: 'all 0.3s',
                }}>Вперёд →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArticlesPage;
